import {Letter} from 'APIs/letrusAPI/letters/types';

enum ENEMCompetenceDetails {
  'Competência 1' = 'Dominio da linguagem padrão',
  'Competência 2' = 'Adequação ao tema e ao gênero',
  'Competência 3' = 'Projeto de texto',
  'Competência 4' = 'Uso dos mecanismos linguisticos',
  'Competência 5' = 'Proposta de intervenção',
}

enum MultigenreCompetenceDetails {
  'Competência 1' = 'Dominio da linguagem padrão',
  'Competência 2' = 'Adesão à proposta',
  'Competência 3' = 'Adesão a coletânea',
  'Competência 4' = 'Desenvolvimento do gênero',
  'Competência 5' = 'Coesão do texto',
  'Competência 6' = 'Coerência do texto',
}

enum FuvestCompetenceDetails {
  'Competência 1' = 'Desenvolvimento do tema',
  'Competência 2' = 'Coerência dos argumentos',
  'Competência 3' = 'Correção Gramatical',
}

function useComparisonLetter(letter?: Letter) {
  const lastYear = new Date().getFullYear() - 1;
  const reviewGrid = letter?.review_grid_name || 'ENEM';
  const isZeroedLetter = letter?.total === 0;
  const isEmptyLetter = !letter?.values.length;

  function getAverageColor(average: number) {
    if (average > 0) return 'positive';
    if (average < 0) return 'negative';

    return '';
  }

  function addDiffPrefix(average: number) {
    return average > 0 ? `+${average}` : average;
  }

  const sortedCompetences =
    letter?.values.sort((competenceA, competenceB) =>
      competenceA.name.localeCompare(competenceB.name),
    ) ?? [];

  const activityTotalScore = sortedCompetences.reduce(
    (total, competence) => total + competence.second_value,
    0,
  );

  const letrusAverageScore =
    Math.floor(activityTotalScore / sortedCompetences.length) || 0;

  const averageText = {
    ENEM: ` a nota média em redação no ENEM ${lastYear} foi ${activityTotalScore}`,
    Multigênero: ` a média Letrus é de ${letrusAverageScore} pontos`,
    Fuvest: ` a média Letrus é de ${letrusAverageScore} pontos`,
  };

  const descriptions = {
    ENEM: {
      competenceScore:
        'Estas são suas notas por habilidade no ENEM, que avalia cinco habilidades com pontuações entre 0 e 200. Aqui, você verá seus pontos fortes e áreas a melhorar. Se sua pontuação estiver em branco, pode ser que você não tenha feito essa parte da prova. Quando fizer novamente, compare com a média oficial do ENEM.',
      referenceScore:
        ' Aqui, comparamos suas notas com as médias de cada habilidade do último ENEM. Isso ajuda a identificar áreas onde você pode melhorar. Esta é só uma forma de ver seus resultados. Lembre-se de considerar todas as suas atividades ao longo do ano, pois você pode ter tirado notas muito diferentes.',
    },
    Multigênero: {
      competenceScore:
        'Aqui você verá suas notas nas escritas da Letrus, avaliadas em 6 critérios, com pontuação de 2 a 10 pontos. Se uma nota estiver em branco, você pode não ter feito a atividade. Ao escrever novamente, compare seus resultados com a média Letrus.',
      referenceScore:
        'Comparamos suas notas com as médias da Letrus em cada critério para identificar os seus pontos fortes e áreas de melhoria. Isso ajuda a focar no seu desenvolvimento. Lembre-se de considerar todas as atividades do ano, pois suas notas podem variar.',
    },
    Fuvest: {
      competenceScore:
        'Aqui você verá suas notas nas escritas da Letrus, avaliadas em 3 critérios, com pontuação de 3 a 15 nos critérios 1 e 2, e de 4 a 20 no critério 3. Se uma nota estiver em branco, você pode não ter realizado a atividade. Ao escrever novamente, compare seus resultados com a média Letrus.',
      referenceScore: `Comparamos suas notas com a Média Letrus FUVEST ${lastYear} em cada critério para identificar os seus pontos fortes e áreas de melhoria. Isso ajuda a focar no seu desenvolvimento. Lembre-se de considerar todas as atividades do ano, pois suas notas podem variar.`,
    },
  };

  const averageTableHeader = {
    ENEM: `Média ENEM ${lastYear}`,
    Multigênero: `Média Olimpíadas Letrus ${lastYear}`,
    Fuvest: `Média Letrus FUVEST ${lastYear}`,
  };

  const competenceDetails = {
    ENEM: ENEMCompetenceDetails,
    Multigênero: MultigenreCompetenceDetails,
    Fuvest: FuvestCompetenceDetails,
  };

  const enrichedCompetencies = sortedCompetences.map((competence) => ({
    name: competence.name,
    comment: competence.comment,
    studentScore: competence.first_value,
    referenceScore: competence.second_value,
    diffColor: getAverageColor(competence.average),
    scoreDifference: addDiffPrefix(competence.average),
    detail: competenceDetails[reviewGrid][competence.name],
  }));

  return {
    reviewGrid,
    schoolName: letter?.school_name,
    studentName: letter?.student_name,
    competences: enrichedCompetencies,
    averageText: averageText[reviewGrid],
    descriptions: descriptions[reviewGrid],
    isEmpty: isEmptyLetter || isZeroedLetter,
    schoolGroupName: letter?.school_group_name,
    totalAverageScore: Math.floor(letter?.total ?? 0),
    averageTableHeader: averageTableHeader[reviewGrid],
  };
}

export default useComparisonLetter;
